/**
 * Generativní UI karta pro mobilní zobrazení nabídky.
 * Řemeslník vidí vygenerovaná data; chybějící hodnoty (null) jsou červeně orámované inputy.
 * Tlačítko Odeslat je disabled, dokud nejsou vyplněna všechna povinná pole.
 */
import { useState, useCallback } from "react";
import { Send } from "lucide-react";
import type { QuoteData, DynamicParameter } from "@/lib/quoteSchema";

interface Props {
  /** Vygenerovaná data z LLM */
  data: QuoteData;
  /** Callback při odeslání – data jsou kompletní (žádné null u povinných) */
  onSubmit: (data: QuoteData) => void;
  /** Loading stav (např. při ukládání do Supabase) */
  submitting?: boolean;
}

export function DynamicQuoteCard({ data: initialData, onSubmit, submitting = false }: Props) {
  const [data, setData] = useState<QuoteData>(initialData);

  const updateTotalPrice = useCallback((value: string) => {
    const num = value.trim() ? parseInt(value.replace(/\s/g, ""), 10) : null;
    setData((prev) => ({ ...prev, total_price: Number.isNaN(num) ? null : num }));
  }, []);

  const updateStartDate = useCallback((value: string) => {
    setData((prev) => ({ ...prev, start_date: value.trim() || null }));
  }, []);

  const updateEndDate = useCallback((value: string) => {
    setData((prev) => ({ ...prev, end_date: value.trim() || null }));
  }, []);

  const updateDynamicParam = useCallback((id: string, value: string | number | boolean | null) => {
    setData((prev) => ({
      ...prev,
      dynamic_parameters: prev.dynamic_parameters.map((p) =>
        p.id === id ? { ...p, value } : p
      ),
    }));
  }, []);

  /** Kontrola: jsou všechna povinná pole vyplněna? */
  const isComplete = (): boolean => {
    if (data.total_price == null || data.total_price <= 0) return false;
    for (const p of data.dynamic_parameters) {
      if (p.value === null || p.value === "") return false;
    }
    return true;
  };

  const handleSubmit = () => {
    if (!isComplete() || submitting) return;
    onSubmit(data);
  };

  return (
    <div className="border border-border rounded-xl bg-card overflow-hidden shadow-sm">
      {/** Header */}
      <div className="px-4 py-3 border-b border-border bg-muted/30">
        <h3 className="font-semibold text-foreground text-sm">Nabídka</h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          Doplňte červeně označená pole a odešlete zákazníkovi.
        </p>
      </div>

      <div className="p-4 space-y-4">
        {/** Cena – povinné */}
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1.5">
            Celková cena (Kč)
          </label>
          {data.total_price != null && data.total_price > 0 ? (
            <div className="px-3 py-2.5 rounded-lg border border-border bg-muted/20 text-foreground font-semibold">
              {data.total_price.toLocaleString("cs-CZ")} Kč
            </div>
          ) : (
            <input
              type="text"
              inputMode="numeric"
              placeholder="např. 185 000"
              onChange={(e) => updateTotalPrice(e.target.value)}
              className="w-full px-3 py-3 rounded-lg border-2 border-destructive/70 bg-destructive/5 text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-destructive/30"
            />
          )}
        </div>

        {/** Termín – volitelné, ale pokud null, zobrazíme input */}
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1.5">
            Termín od
          </label>
          {data.start_date ? (
            <div className="px-3 py-2.5 rounded-lg border border-border bg-muted/20 text-foreground">
              {data.start_date}
            </div>
          ) : (
            <input
              type="text"
              placeholder="např. květen 2026"
              value={data.start_date ?? ""}
              onChange={(e) => updateStartDate(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-ring"
            />
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1.5">
            Termín do
          </label>
          {data.end_date ? (
            <div className="px-3 py-2.5 rounded-lg border border-border bg-muted/20 text-foreground">
              {data.end_date}
            </div>
          ) : (
            <input
              type="text"
              placeholder="např. červen 2026"
              value={data.end_date ?? ""}
              onChange={(e) => updateEndDate(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-ring"
            />
          )}
        </div>

        {/** Dynamické parametry */}
        {data.dynamic_parameters.length > 0 && (
          <div className="space-y-3">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Parametry
            </span>
            {data.dynamic_parameters.map((param) => (
              <DynamicParamField
                key={param.id}
                param={param}
                onChange={(value) => updateDynamicParam(param.id, value)}
              />
            ))}
          </div>
        )}
      </div>

      {/** Tlačítko Odeslat */}
      <div className="p-4 pt-0">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!isComplete() || submitting}
          className="w-full flex items-center justify-center gap-2 py-4 bg-primary text-primary-foreground rounded-xl text-base font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation min-h-[52px]"
        >
          {submitting ? (
            <span className="animate-pulse">Odesílám...</span>
          ) : (
            <>
              <Send className="w-5 h-5" />
              Odeslat nabídku zákazníkovi
            </>
          )}
        </button>
        {!isComplete() && (
          <p className="text-[10px] text-muted-foreground text-center mt-2">
            Vyplňte všechna červeně označená pole
          </p>
        )}
      </div>
    </div>
  );
}

/** Jedno políčko dynamického parametru – string/number/boolean */
function DynamicParamField({
  param,
  onChange,
}: {
  param: DynamicParameter;
  onChange: (value: string | number | boolean | null) => void;
}) {
  const isMissing = param.value === null || param.value === "";

  if (param.type === "boolean") {
    const val = param.value;
    return (
      <div>
        <span className="block text-xs font-medium text-muted-foreground mb-2">{param.label}</span>
        {val === true || val === false ? (
          <div className="flex items-center gap-2">
            <span
              className={`px-3 py-2 rounded-lg text-sm font-medium ${
                val ? "bg-primary/10 text-primary border border-primary/30" : "bg-muted/30 text-muted-foreground"
              }`}
            >
              {val ? "Ano" : "Ne"}
            </span>
          </div>
        ) : (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => onChange(true)}
              className="flex-1 py-2.5 rounded-lg border-2 border-destructive/70 bg-destructive/5 text-sm font-medium hover:bg-destructive/10 transition-colors"
            >
              Ano
            </button>
            <button
              type="button"
              onClick={() => onChange(false)}
              className="flex-1 py-2.5 rounded-lg border-2 border-destructive/70 bg-destructive/5 text-sm font-medium hover:bg-destructive/10 transition-colors"
            >
              Ne
            </button>
          </div>
        )}
      </div>
    );
  }

  if (param.type === "date") {
    return (
      <div>
        <label className="block text-xs font-medium text-muted-foreground mb-1">
          {param.label}
        </label>
        {param.value != null && param.value !== "" ? (
          <div className="px-3 py-2.5 rounded-lg border border-border bg-muted/20 text-foreground">
            {String(param.value)}
          </div>
        ) : (
          <input
            type="text"
            placeholder="např. květen 2026 nebo 2026-05-01"
            value={param.value ?? ""}
            onChange={(e) => onChange(e.target.value.trim() || null)}
            className={`w-full px-3 py-2.5 rounded-lg border bg-background text-foreground text-sm outline-none focus:ring-2 ${
              isMissing
                ? "border-2 border-destructive/70 bg-destructive/5 focus:ring-destructive/30"
                : "border-border focus:ring-ring"
            }`}
          />
        )}
      </div>
    );
  }

  if (param.type === "number") {
    return (
      <div>
        <label className="block text-xs font-medium text-muted-foreground mb-1">
          {param.label}
        </label>
        {param.value != null && param.value !== "" ? (
          <div className="px-3 py-2.5 rounded-lg border border-border bg-muted/20 text-foreground">
            {String(param.value)}
          </div>
        ) : (
          <input
            type="number"
            inputMode="numeric"
            placeholder={`Zadejte ${param.label.toLowerCase()}`}
            value={param.value ?? ""}
            onChange={(e) => {
              const v = e.target.value;
              const num = v.trim() ? parseFloat(v) : null;
              onChange(num);
            }}
            className={`w-full px-3 py-2.5 rounded-lg border bg-background text-foreground text-sm outline-none focus:ring-2 ${
              isMissing
                ? "border-2 border-destructive/70 bg-destructive/5 focus:ring-destructive/30"
                : "border-border focus:ring-ring"
            }`}
          />
        )}
      </div>
    );
  }

  // string
  return (
    <div>
      <label className="block text-xs font-medium text-muted-foreground mb-1">
        {param.label}
      </label>
      {param.value != null && param.value !== "" ? (
        <div className="px-3 py-2.5 rounded-lg border border-border bg-muted/20 text-foreground">
          {String(param.value)}
        </div>
      ) : (
        <input
          type="text"
          placeholder={`Zadejte ${param.label.toLowerCase()}`}
          value={param.value ?? ""}
          onChange={(e) => onChange(e.target.value.trim() || null)}
          className={`w-full px-3 py-2.5 rounded-lg border bg-background text-foreground text-sm outline-none focus:ring-2 ${
            isMissing
              ? "border-2 border-destructive/70 bg-destructive/5 focus:ring-destructive/30"
              : "border-border focus:ring-ring"
          }`}
        />
      )}
    </div>
  );
}
